import React from 'react';
import type { CSS } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import { AddToQueue } from '@components/main-components/media/icons/AddToQueue';
import { ToastContext } from '@context/toast';
import { addTrackAudioToQueue } from '@store/audio/AudioFetcher';
import { addTrackToQueue } from '@store/audio/AudioQueue';
import { DataTestId } from '@utils/test';
import { DropdownMenuItem, MenuItemLabel } from '../GlobalDropdown';

export const AddToQueueMenuItem = ({
  trackId,
  trackIds,
  label = 'Add to queue',
  disabled,
  css,
  emitAnalytics,
}: {
  trackId?: string;
  trackIds?: string[];
  label?: string;
  disabled: boolean;
  css?: CSS;
  emitAnalytics: () => void;
}) => {
  const { openToast } = ToastContext.useContainer();

  const addMultipleTracksToQueue = ({ ids }: { ids: string[] }) => {
    addTrackToQueue(...[...ids].reverse().map(id => ({ trackId: id })));
    openToast({ text: 'Added to Queue', variant: 'success' });
  };

  return (
    <DropdownMenuItem
      data-testid={DataTestId.ADD_TO_QUEUE_MENU_ITEM_DROPDOWN}
      disabled={disabled}
      onClick={e => {
        e.stopPropagation();
        emitAnalytics();
        if (!!trackIds) {
          return addMultipleTracksToQueue({ ids: trackIds });
        } else if (!!trackId) {
          if (trackId) {
            return addTrackAudioToQueue({ trackId: trackId })
              .then(() => {
                openToast({ text: 'Added to Queue', variant: 'success' });
              })
              .catch(() => {
                // TODO: https://linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
                openToast({ text: 'Something went wrong', variant: 'error' });
              });
          }
          return openToast({
            text: 'Missing Track ID. Cannot add track to queue',
            variant: 'warning',
          });
        }

        // TODO: https://linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
        return openToast({ text: 'Unable to add track to queue', variant: 'error' });
      }}
    >
      <IconWrapper isDisabled={disabled}>
        <AddToQueue />
      </IconWrapper>
      <MenuItemLabel css={css}>{label}</MenuItemLabel>
    </DropdownMenuItem>
  );
};

const IconWrapper = styled('span', {
  color: '$neutral800',
  variants: {
    isDisabled: {
      true: {
        color: '$neutral400',
      },
    },
  },
});
